import { ErrorCodes, PrimaryKeyValueModel } from '@shared';
import { firstValueFrom, iif, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DeleteIntervalService } from '../services';
import { IntervalTableEnum, ValidationKeys } from '../../../../dynamic-form/validations/models';
import { DeleteChainParams, IDeleteValidatorHandler, IntervalChildData, IValidationsRule } from './models';
import { LoggingService } from '@shared/logging';
import { BusinessLayerService } from '../../../services/business-layer.service';

export class IntervalRelatedCheck implements IDeleteValidatorHandler {
	private next: IDeleteValidatorHandler;

	public constructor(
		private deleteDataService: DeleteIntervalService,
		private loggerService: LoggingService,
		private businessLayerService: BusinessLayerService
	) {}

	public setNext(nextHandler: IDeleteValidatorHandler) {
		this.next = nextHandler;
	}

	public async handle(deleteChainParams: DeleteChainParams): Promise<IValidationsRule | null> {
		try {
			const intervalRelatedValidation: IValidationsRule | null = await firstValueFrom(
				of(this.isIntervalTable(deleteChainParams.tableName)).pipe(
					switchMap((isIntervalTable: boolean) =>
						iif(
							() => isIntervalTable,
							this.businessLayerService
								.getPrimaryKeyAndValue$(
									deleteChainParams.workfileId,
									deleteChainParams.projectNumber,
									deleteChainParams.entityName,
									Number(deleteChainParams.entryId)
								)
								.pipe(
									switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
										this.deleteDataService
											.checkIntervalDataDependencies$(
												deleteChainParams.tableName,
												deleteChainParams.projectNumber,
												primaryKeyValue,
												deleteChainParams.childEntryId,
												deleteChainParams.depthFrom,
												deleteChainParams.depthTo
											)
											.pipe(
												switchMap((childData: IntervalChildData[]) => {
													const underlyingIntervalTable = childData.find(
														(tableData: IntervalChildData) => tableData.tableHasData
													)?.tableName;

													return underlyingIntervalTable
														? underlyingIntervalTable.toLowerCase() ===
														  IntervalTableEnum.HoleIntervalMinor.toLowerCase()
															? of({
																	validationKey:
																		ValidationKeys.validationRules
																			.intervalHasMinorDataRule
															  })
															: of({
																	validationKey:
																		ValidationKeys.validationRules
																			.intervalHasChildDataRule
															  })
														: of(null);
												})
											)
									)
								),
							of(null)
						)
					)
				)
			);

			if (intervalRelatedValidation) {
				return intervalRelatedValidation;
			}

			if (this.next) {
				return await this.next.handle(deleteChainParams);
			}

			return null;
		} catch (error) {
			this.loggerService.error(ErrorCodes.IntervalRelatedError, error);
			throw error;
		}
	}

	private isIntervalTable(tableName: string): boolean {
		return (
			IntervalTableEnum.HoleIntervalMajor.toLowerCase() === tableName.toLowerCase() ||
			IntervalTableEnum.HoleIntervalMinor.toLowerCase() === tableName.toLowerCase()
		);
	}
}
